import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Switch,
  Stack,
  Tooltip,
  Grid,
  Divider
} from '@mui/joy';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaUser, 
  FaUsers, 
  FaBuilding,
  FaTools,
  FaChartBar,
  FaCheck,
  FaTimes,
  FaKey
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { directSupabase } from '../lib/supabaseClient';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  group: 'staff' | 'customer';
  role: 'admin' | 'user' | 'document' | 'fitter' | 'labourer' | 'supplier' | null;
  department: string | null;
  position: string | null;
  is_active: boolean;
  can_assign_maintenance: boolean;
  can_view_reports: boolean;
  created_at: string;
  updated_at: string;
}

interface UserWithProfile {
  id: string;
  email: string;
  created_at: string;
  profile: Profile | null;
}

const UserManagement: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [filterGroup, setFilterGroup] = useState<'all' | 'staff' | 'customer'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    group: 'staff' as 'staff' | 'customer',
    role: 'admin' as 'admin' | 'user' | 'document' | 'fitter' | 'labourer' | 'supplier',
    department: '',
    position: '',
    is_active: true,
    can_assign_maintenance: false,
    can_view_reports: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  // Note: This component now has full user management capabilities
  // - Creates users in Supabase Auth
  // - Creates profiles with proper permissions
  // - Sends password reset emails to new users
  // - Only admins can access this functionality

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all profiles (this doesn't require admin privileges)
      const { data: profiles, error: profilesError } = await directSupabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        throw new Error(profilesError.message);
      }

      // Convert profiles to UserWithProfile format
      const usersWithProfiles: UserWithProfile[] = (profiles || []).map(profile => ({
        id: profile.id,
        email: profile.email || '',
        created_at: profile.created_at,
        profile
      }));

      setUsers(usersWithProfiles);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the edge function to create user and profile
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('sb-access-token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/create-user', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: formData.email,
          password: 'temporary-password-123', // User will need to reset this
          full_name: formData.full_name,
          phone: formData.phone,
          group: formData.group,
          role: formData.role,
          department: formData.department,
          position: formData.position,
          is_active: formData.is_active,
          can_assign_maintenance: formData.can_assign_maintenance,
          can_view_reports: formData.can_view_reports
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create user and profile');
      }

      setSuccess('User and profile created successfully! User will need to reset their password.');
      setModalOpen(false);
      resetForm();
      loadUsers();
    } catch (err) {
      console.error('Error creating user and profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to create user and profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!editingProfile) return;

    try {
      setLoading(true);
      setError(null);

      // Update profile data
      const updateData = {
        full_name: formData.full_name,
        phone: formData.phone,
        group: formData.group,
        role: formData.group === 'staff' ? formData.role : null,
        department: formData.department,
        position: formData.position,
        is_active: formData.is_active,
        can_assign_maintenance: formData.can_assign_maintenance,
        can_view_reports: formData.can_view_reports
      };

      const { error } = await directSupabase
        .from('profiles')
        .update(updateData)
        .eq('id', editingProfile.id);

      if (error) {
        throw new Error(error.message);
      }

      // Update password if provided
      if (formData.password.trim()) {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('sb-access-token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const passwordResponse = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/update-password', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userId: editingProfile.id,
            password: formData.password
          })
        });

        if (!passwordResponse.ok) {
          const passwordError = await passwordResponse.json();
          throw new Error(passwordError.error || 'Failed to update password');
        }
      }

      setSuccess('Profile updated successfully!');
      setModalOpen(false);
      setEditingProfile(null);
      resetForm();
      loadUsers();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = (profile: Profile) => {
    setEditingProfile(profile);
    setFormData({
      full_name: profile.full_name || '',
      email: profile.email || '',
      password: '', // Password is not editable
      phone: profile.phone || '',
      group: profile.group,
      role: (profile.role as any) || 'user',
      department: profile.department || '',
      position: profile.position || '',
      is_active: profile.is_active,
      can_assign_maintenance: profile.can_assign_maintenance,
      can_view_reports: profile.can_view_reports
    });
    setModalOpen(true);
  };

  const handleDeleteProfile = async (profileId: string) => {
    setDeleteProfileId(profileId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteProfile = async () => {
    if (!deleteProfileId) return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await directSupabase
        .from('profiles')
        .delete()
        .eq('id', deleteProfileId);

      if (error) {
        throw new Error(error.message);
      }

      setSuccess('Profile deleted successfully!');
      loadUsers();
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete profile');
    } finally {
      setLoading(false);
    }
    
    setShowDeleteConfirm(false);
    setDeleteProfileId(null);
  };

  const handleResetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('sb-access-token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/reset-password', {
        method: 'POST',
        headers,
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send password reset email');
      }

      setSuccess('Password reset email sent successfully!');
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      phone: '',
      group: 'staff',
      role: 'admin',
      department: '',
      position: '',
      is_active: true,
      can_assign_maintenance: false,
      can_view_reports: false
    });
  };

  const filteredUsers = users.filter(user => {
    const profile = user.profile;
    if (!profile) return true;
    
    if (filterGroup !== 'all' && profile.group !== filterGroup) return false;
    if (filterActive !== 'all') {
      if (filterActive === 'active' && !profile.is_active) return false;
      if (filterActive === 'inactive' && profile.is_active) return false;
    }
    
    return true;
  });

  const getRoleColor = (role: string | null) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'fitter': return 'success';
      case 'labourer': return 'success';
      case 'supplier': return 'warning';
      case 'document': return 'primary';
      case 'manager': return 'warning';
      case 'worker': return 'primary';
      case 'user': return 'neutral';
      default: return 'neutral';
    }
  };

  const getGroupColor = (group: string) => {
    return group === 'staff' ? 'primary' : 'neutral';
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography level="h4" sx={{ mb: 1 }}>
            User Management
          </Typography>
          <Typography level="body-sm" color="neutral">
            Manage staff profiles and access permissions
          </Typography>
        </Box>
        <Button
          startDecorator={<IconWrapper icon={FaPlus} />}
          onClick={() => {
            setEditingProfile(null);
            resetForm();
            setModalOpen(true);
          }}
        >
          Add User Profile
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Group</FormLabel>
                <Select
                  value={filterGroup}
                  onChange={(_, value) => setFilterGroup(value as any)}
                >
                  <Option value="all">All Groups</Option>
                  <Option value="staff">Staff</Option>
                  <Option value="customer">Customers</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Status</FormLabel>
                <Select
                  value={filterActive}
                  onChange={(_, value) => setFilterActive(value as any)}
                >
                  <Option value="all">All Status</Option>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Users Table */}
      <Card>
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr',
            gap: 2,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontWeight: 'bold',
            fontSize: 'sm'
          }}>
            <Box>User</Box>
            <Box>Group</Box>
            <Box>Role</Box>
            <Box>Department</Box>
            <Box>Permissions</Box>
            <Box>Status</Box>
            <Box>Actions</Box>
          </Box>
          
          {filteredUsers.map((user) => (
            <Box key={user.id} sx={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr',
              gap: 2,
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'background.level1'
              }
            }}>
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                  {user.profile?.full_name || 'No Name'}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {user.email}
                </Typography>
                {user.profile?.phone && (
                  <Typography level="body-xs" color="neutral">
                    {user.profile.phone}
                  </Typography>
                )}
              </Box>
              
              <Box>
                <Chip
                  size="sm"
                  color={getGroupColor(user.profile?.group || 'customer')}
                  variant="soft"
                >
                  {user.profile?.group || 'No Profile'}
                </Chip>
              </Box>
              
              <Box>
                {user.profile?.role && (
                  <Chip
                    size="sm"
                    color={getRoleColor(user.profile.role)}
                    variant="soft"
                  >
                    {user.profile.role}
                  </Chip>
                )}
              </Box>
              
              <Box>
                <Typography level="body-sm">
                  {user.profile?.department || '-'}
                </Typography>
                {user.profile?.position && (
                  <Typography level="body-xs" color="neutral">
                    {user.profile.position}
                  </Typography>
                )}
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {user.profile?.can_assign_maintenance && (
                    <Tooltip title="Can Assign Maintenance">
                      <IconWrapper icon={FaTools} />
                    </Tooltip>
                  )}
                  {user.profile?.can_view_reports && (
                    <Tooltip title="Can View Reports">
                      <IconWrapper icon={FaChartBar} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              
              <Box>
                <Chip
                  size="sm"
                  color={user.profile?.is_active ? 'success' : 'neutral'}
                  variant="soft"
                  startDecorator={
                    user.profile?.is_active ? 
                    <IconWrapper icon={FaCheck} /> : 
                    <IconWrapper icon={FaTimes} />
                  }
                >
                  {user.profile?.is_active ? 'Active' : 'Inactive'}
                </Chip>
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Reset Password">
                    <IconButton
                      size="sm"
                      onClick={() => handleResetPassword(user.email)}
                    >
                      <IconWrapper icon={FaKey} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Profile">
                    <IconButton
                      size="sm"
                      onClick={() => user.profile && handleEditProfile(user.profile)}
                      disabled={!user.profile}
                    >
                      <IconWrapper icon={FaEdit} />
                    </IconButton>
                  </Tooltip>
                  {user.profile && (
                    <Tooltip title="Delete Profile">
                      <IconButton
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteProfile(user.profile!.id)}
                      >
                        <IconWrapper icon={FaTrash} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalDialog size="lg">
          <DialogTitle>
            {editingProfile ? 'Edit User Profile' : 'Create User Profile'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      value={formData.full_name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, full_name: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      value={formData.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
                      disabled={!!editingProfile}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingProfile ? "Leave blank to keep current password" : "Enter password"}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      value={formData.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Group</FormLabel>
                    <Select
                      value={formData.group}
                      onChange={(_, value) => setFormData({ ...formData, group: value as any })}
                    >
                      <Option value="staff">Staff</Option>
                      <Option value="customer">Customer</Option>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.group === 'staff' && (
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Role</FormLabel>
                      <Select
                        value={formData.role === 'labourer' ? 'fitter' : formData.role}
                        onChange={(_, value) => {
                          if (value === 'fitter') {
                            setFormData({
                              ...formData,
                              role: formData.role === 'labourer' ? 'labourer' : 'fitter',
                            });
                            return;
                          }
                          setFormData({ ...formData, role: value as any });
                        }}
                      >
                        <Option value="admin">Admin</Option>
                        <Option value="fitter">Fitter</Option>
                        <Option value="supplier">Supplier</Option>
                        <Option value="document">Document</Option>
                        {formData.role === 'user' && <Option value="user">User</Option>}
                        {formData.role && !['admin', 'fitter', 'labourer', 'supplier', 'document', 'user'].includes(formData.role) && (
                          <Option value={formData.role}>{formData.role}</Option>
                        )}
                      </Select>
                    </FormControl>
                    {(formData.role === 'fitter' || formData.role === 'labourer') && (
                      <FormControl sx={{ mt: 1.5 }}>
                        <FormLabel>Fitter sub-role</FormLabel>
                        <Select
                          value={formData.role === 'labourer' ? 'labourer' : 'fitter'}
                          onChange={(_, value) => setFormData({ ...formData, role: (value as 'fitter' | 'labourer') || 'fitter' })}
                        >
                          <Option value="fitter">Fitter</Option>
                          <Option value="labourer">Labourer</Option>
                        </Select>
                        <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                          Labourers see the same assigned jobs and comments. They can comment, but cannot complete jobs.
                        </Typography>
                      </FormControl>
                    )}
                  </Grid>
                )}
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Department</FormLabel>
                    <Input
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Position</FormLabel>
                    <Input
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Divider />

              <Typography level="title-sm">Permissions</Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl>
                  <FormLabel>Active Status</FormLabel>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                </FormControl>
                
                {formData.group === 'staff' && (
                  <>
                    <FormControl>
                      <FormLabel>Can Assign Maintenance</FormLabel>
                      <Switch
                        checked={formData.can_assign_maintenance}
                        onChange={(e) => setFormData({ ...formData, can_assign_maintenance: e.target.checked })}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Can View Reports</FormLabel>
                      <Switch
                        checked={formData.can_view_reports}
                        onChange={(e) => setFormData({ ...formData, can_view_reports: e.target.checked })}
                      />
                    </FormControl>
                  </>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setModalOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={editingProfile ? handleUpdateProfile : handleCreateProfile}
              loading={loading}
            >
              {editingProfile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <ModalDialog>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this user profile? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={confirmDeleteProfile} loading={loading}>
              Delete Profile
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default UserManagement; 